// @ts-nocheck
import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Card,
    CardContent,
    Grid,
    Divider,
    Stack,
    IconButton,
    InputAdornment,
    Chip,
    CircularProgress,
    Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { NoDataIllustration } from '../../../assets/icons';
import { useGetCardBins, CardBinResponse } from '../../../hooks';
import { useSetSnackBar } from '../../../store/Snackbar.state';

export const BinQuery = () => {
    const [binInput, setBinInput] = useState('');
    const [result, setResult] = useState<CardBinResponse | null>(null);
    const [hasSearched, setHasSearched] = useState(false);

    const setSnackbar = useSetSnackBar();
    const { mutate: getCardBins, isLoading } = useGetCardBins();

    const handleSearch = () => {
        if (!binInput || binInput.length < 6) {
            setSnackbar({
                severity: "warning",
                description: "Lütfen en az 6 haneli bir BIN giriniz.",
                isOpen: true,
            });
            return;
        }

        setHasSearched(true);
        setResult(null);

        getCardBins({
            bin: parseInt(binInput, 10),
            id: 0,
            bankCode: "",
            cardRangeStart: "",
            cardRangeEnd: ""
        }, {
            onSuccess: (data) => {
                if (data.isSuccess && data.data && data.data.length > 0) {
                    // API returns a list, we take the first match for the detail page
                    setResult(data.data[0]);
                } else {
                    setResult(null);
                    if (!data.isSuccess && data.message) {
                        setSnackbar({
                            severity: "error",
                            description: data.message,
                            isOpen: true,
                        });
                    }
                }
            },
            onError: (error: any) => {
                setResult(null);
                setSnackbar({
                    severity: "error",
                    description: error?.message || "Sorgulama sırasında bir hata oluştu.",
                    isOpen: true,
                });
            }
        });
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setSnackbar({
            severity: "success",
            description: "Kopyalandı: " + text,
            isOpen: true,
        });
    };

    const renderSkeletons = () => (
        <Grid container spacing={3}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={12} md={i > 3 ? 6 : 4} lg={i > 3 ? 8 : 4} key={i}>
                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '12px' }} />
                </Grid>
            ))}
        </Grid>
    );

    return (
        <Box p={4} className="font-sans">
            {/* Header Section */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4" fontWeight="900" gutterBottom className="text-[#111418]">
                        BIN Sorgula
                    </Typography>
                    <Typography variant="body2" color="textSecondary" maxWidth="600px">
                        Kart numarasının ilk 6-8 hanesini girerek kart markasını, tipini ve düzenleyen banka bilgilerini tanımlayın.
                    </Typography>
                </Box>
            </Box>

            {/* Search Box */}
            <Card variant="outlined" sx={{ mb: 4, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" gap={2}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="6-8 haneli BIN giriniz (örn. 542694)"
                            value={binInput}
                            disabled={isLoading}
                            onChange={(e) => setBinInput(e.target.value.replace(/\D/g, '').slice(0, 8))}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: '8px',
                                    fontFamily: 'monospace',
                                    fontSize: '1.1rem',
                                    letterSpacing: '0.1em'
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={isLoading || binInput.length < 6}
                            onClick={handleSearch}
                            sx={{ borderRadius: '8px', px: 4, textTransform: 'none', fontWeight: 'bold', minWidth: '140px' }}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sorgula'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {isLoading ? (
                <Box mt={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                        <Skeleton width="150px" height={32} />
                        <Skeleton width="100px" height={32} />
                    </Box>
                    {renderSkeletons()}
                </Box>
            ) : result ? (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" borderColor="divider" pb={2}>
                        <Typography variant="h6" fontWeight="bold">Sorgu Sonuçları</Typography>
                    </Box>

                    <Grid container spacing={3} mb={3}>
                        {/* Network & Brand */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: '12px' }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" mb={2}>
                                        <Typography variant="caption" fontWeight="bold" color="textSecondary" textTransform="uppercase">
                                            Ağ ve Kart Markası
                                        </Typography>
                                        <CreditCardIcon color="primary" />
                                    </Box>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        {result.paymentSystemName || result.paymentSystem}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {result.brandName !== "Marka Yok" ? result.brandName : result.paymentSystemName}
                                    </Typography>
                                    <Box mt={2} height={40} display="flex" alignItems="center">
                                        {result.urlCardAssociationLogo ? (
                                            <img src={result.urlCardAssociationLogo} alt={result.paymentSystemName} style={{ maxHeight: '100%', maxWidth: '100px' }} />
                                        ) : (
                                            <Typography variant="caption">Logo Yok</Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Card Type */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: '12px' }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" mb={2}>
                                        <Typography variant="caption" fontWeight="bold" color="textSecondary" textTransform="uppercase">
                                            Kart Tipi
                                        </Typography>
                                        <PaymentsIcon color="primary" />
                                    </Box>
                                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                                        {result.cardTypeName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {result.cardSubTypeName}
                                    </Typography>
                                    <Box mt={2} height={40} display="flex" alignItems="center">
                                        {result.urlCardBrandLogo ? (
                                            <img src={result.urlCardBrandLogo} alt={result.brandCode} style={{ maxHeight: '100%', maxWidth: '100px' }} />
                                        ) : (
                                            <Typography variant="caption">Logo Yok</Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Issuing Bank */}
                        <Grid item xs={12} md={6} lg={4}>
                            <Card variant="outlined" sx={{ height: '100%', borderRadius: '12px' }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" mb={2}>
                                        <Typography variant="caption" fontWeight="bold" color="textSecondary" textTransform="uppercase">
                                            Düzenleyen Banka
                                        </Typography>
                                        <AccountBalanceIcon color="primary" />
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold" noWrap title={result.bankName}>
                                        {result.bankName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Banka Kodu: {result.bankCode}
                                    </Typography>
                                    <Box mt={2} height={40} display="flex" alignItems="center">
                                        {result.urlBankLogo ? (
                                            <img src={result.urlBankLogo} alt={result.bankCode} style={{ maxHeight: '100%', maxWidth: '100px' }} />
                                        ) : (
                                            <Typography variant="caption">Logo Yok</Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Detailed Data Table */}
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight="bold">Detaylı Veri Özeti</Typography>
                        </Box>

                        <Card variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: '#f9fafb' }}>
                                    <tr>
                                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', color: '#6b7280' }}>Özellik</th>
                                        <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', color: '#6b7280' }}>Değer</th>
                                        <th style={{ padding: '12px 24px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', color: '#6b7280' }}>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { label: 'Banka Kodu', value: result.bankCode },
                                        { label: 'Kart Uzunluğu', value: `${result.cardNumberLen} hane` },
                                        { label: 'BIN Aralığı', value: `${result.cardRangeStart} - ${result.cardRangeEnd}` },
                                        { label: 'Marka Kodu', value: result.brandCode },
                                        { label: 'Ödeme Sistemi', value: `${result.paymentSystemName} (${result.paymentSystem})` },
                                    ].map((row, index) => (
                                        <tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
                                            <td style={{ padding: '16px 24px', fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>{row.label}</td>
                                            <td style={{ padding: '16px 24px', fontSize: '0.875rem', color: '#111827', fontFamily: 'monospace' }}>{row.value}</td>
                                            <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                                <IconButton size="small" onClick={() => handleCopy(row.value)}>
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    </Box>
                </>
            ) : hasSearched && (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={8}
                    bgcolor="white"
                    borderRadius="12px"
                    border={1}
                    borderColor="divider"
                >
                    <NoDataIllustration style={{ width: 200, height: 'auto', marginBottom: '24px', opacity: 0.8 }} />
                    <Typography variant="h6" fontWeight="bold" color="textPrimary">Sonuç Bulunamadı</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center', maxWidth: '400px' }}>
                        Girdiğiniz BIN ({binInput}) ile eşleşen bir kart verisi bulunamadı. Lütfen numarayı kontrol edip tekrar deneyiniz.
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{ mt: 3, textTransform: 'none' }}
                        onClick={() => {
                            setBinInput('');
                            setHasSearched(false);
                        }}
                    >
                        Temizle ve Yeni Ara
                    </Button>
                </Box>
            )}
        </Box>
    );
};